import React, { useEffect, useState } from "react";
import { Button, Empty, Spin } from "antd";

import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";
import TreatmentPlanListRowInsuranceActions from "../TreatmentPlans/component/TreatmentPlanListRowInsuranceActions";
import { normalizeInsuranceTab } from "../InsuranceRequests/requestTypes";
import { getInsuranceLinkTarget } from "./controller";
import "./deepLink.css";

/**
 * Where "Open in Smilepass" lands.
 *
 * An FTC emails a clinic a card summarising a breakdown, estimate or claim
 * follow-up; this page is the link on it. It mounts the insurance modal headless — the same reuse FTC
 * Tasks already does — so nothing on any existing surface changes.
 *
 * ⚠️ The URL grants nothing. The patient comes from a session-scoped endpoint that
 * refuses anyone without access to that clinic, because this link travels by email
 * and will eventually be read by the wrong person. A logged-out visitor is sent to
 * sign in and returned here; a signed-in visitor from another practice is told the
 * record isn't available, with no hint that it exists.
 */

function readParams() {
  // Hash routing: the query lives after the route, not in window.location.search.
  const hash = window.location.hash || "";
  const q = hash.indexOf("?") === -1 ? "" : hash.slice(hash.indexOf("?") + 1);
  const out = {};
  q.split("&").forEach(function (pair) {
    if (!pair) return;
    const i = pair.indexOf("=");
    const k = i === -1 ? pair : pair.slice(0, i);
    out[decodeURIComponent(k)] = i === -1 ? "" : decodeURIComponent(pair.slice(i + 1));
  });
  return out;
}

export default function InsuranceLinkPage() {
  const [state, setState] = useState({ status: "loading" });
  const params = readParams();
  // One clamp for every tab the modal has, so a link to the Claims tab lands
  // there and an unknown value still falls back to Breakdowns.
  const tab = normalizeInsuranceTab(params.tab);

  useEffect(function () {
    let cancelled = false;
    (async function () {
      if (!params.patient) {
        setState({ status: "bad" });
        return;
      }
      const res = await getInsuranceLinkTarget(params.patient, params.policy);
      if (cancelled) return;
      if (res.status >= 200 && res.status < 300 && res.json) {
        setState({ status: "ready", data: res.json });
      } else if (res.status === 401 || res.status === 403) {
        // Session expired between the click and the fetch.
        window.location.hash = `#/login?next=${encodeURIComponent(window.location.hash.replace(/^#/, ""))}`;
      } else {
        setState({ status: "denied" });
      }
    })();
    return function () { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.patient, params.policy]);

  if (state.status === "loading") {
    return (
      <DashboardLayout>
        <div className="deeplink-center"><Spin size="large" /></div>
      </DashboardLayout>
    );
  }

  if (state.status !== "ready") {
    // One message for "no such record" and for "not your clinic" — the link may
    // have been forwarded, and which of the two it is isn't the reader's business.
    return (
      <DashboardLayout>
        <div className="deeplink-center">
          <Empty
            description={
              <>
                <p style={{ marginBottom: 4 }}>This record isn’t available to your account.</p>
                <p style={{ color: "#848696", fontSize: 13 }}>
                  It may belong to another clinic, or the link may be out of date.
                </p>
              </>
            }
          >
            <Button type="primary" onClick={() => { window.location.hash = "#/treatment-plans"; }}>
              Go to Treatment Planning
            </Button>
          </Empty>
        </div>
      </DashboardLayout>
    );
  }

  const { patient, office_id: officeId, policy_id: policyId } = state.data;

  return (
    <DashboardLayout>
      <div className="deeplink-center">
        <p style={{ color: "#848696" }}>
          Opening {patient.first_name} {patient.last_name}…
        </p>
      </div>
      <TreatmentPlanListRowInsuranceActions
        patient={patient}
        officeId={officeId}
        hideTriggers
        autoOpenInsuranceTab={tab}
        defaultSelectedPolicyId={policyId != null ? policyId : undefined}
        defaultSelectedEntryId={params.entry ? Number(params.entry) : undefined}
        onInsuranceModalClosed={() => { window.location.hash = "#/treatment-plans"; }}
      />
    </DashboardLayout>
  );
}
